import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Link2,
  Search,
  X,
  RefreshCw,
  UserPlus,
  Copy,
  Check,
  Clock,
  CalendarDays,
  Filter,
  Mail,
  UserCheck,
  Link2Off,
  Plus,
  Loader2,
  Link2Icon,
  Ban,
  AlertTriangle,
  Ticket,
} from 'lucide-react';
import { storage } from '../../Shared/Utils/authStorage';
import { generateInviteLink, revokeInviteLink } from '../../Admin/Services/AdminService';
import { GenerateInviteResponseDTO } from '../../Admin/Types/AdminType';
import SocialShareComponent from '../../Admin/Components/SocialShareComponent';
import {
  InvitationStats,
  AllInvitationByInvitedUserResponseDTO,
} from '../Types/CandidateType';
import {
  InvitationListingFilters,
  getInvitationsByInvitedUserFiltered,
} from '../Services/CandidateService';
import Pagination from '../../Shared/Pagination/Pagination';
import { PaginationResponse, PagedStatsResponse } from '../../Shared/Pagination/PaginationResponse';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';

const PAGE_SIZE = 8;

const STATUS_OPTIONS = ['ALL', 'ACTIVE', 'USED', 'EXPIRED', 'REVOKED'];

const emptyStats: InvitationStats = {
  total: 0,
  active: 0,
  used: 0,
  expired: 0,
  revoked: 0,
  registered: 0,
  inviteBalance: 0,
  inviteTotal: 0,
};

const statusBadge = (status?: string) => {
  switch ((status || '').toUpperCase()) {
    case 'ACTIVE':
      return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    case 'USED':
      return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30';
    case 'REVOKED':
      return 'bg-amber-500/10 text-amber-300 border-amber-500/30';
    case 'EXPIRED':
      return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
    default:
      return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
  }
};

const prettifyStatus = (value?: string) =>
  value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const fullName = (row: AllInvitationByInvitedUserResponseDTO) =>
  `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const statusMeta = {
  ACTIVE: { label: 'Active', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  USED: { label: 'Used', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  EXPIRED: { label: 'Expired', cls: 'bg-slate-700/60 text-slate-400 border-slate-600' },
  REVOKED: { label: 'Revoked', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Copy failed', err);
    return false;
  }
};

const CopyButton: React.FC<{ text: string; title?: string }> = ({ text, title }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={title ?? 'Copy to clipboard'}
      className={`flex h-7 w-7 items-center justify-center rounded-lg border transition ${
        copied
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
          : 'border-slate-800 bg-slate-950 text-slate-500 hover:border-cyan-500/40 hover:text-cyan-400'
      }`}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
};

const GenerateInviteModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onGenerated: (data: GenerateInviteResponseDTO) => void;
}> = ({ open, onClose, onGenerated }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'success' | 'error' | ''>('');
  const [result, setResult] = useState<GenerateInviteResponseDTO>({
    inviteCode: '',
    inviteUrl: '',
    expiryAt: '',
  });

  const userId = storage.getUserId() ?? '';

  const handleGenerate = async () => {
    if (!userId) {
      setError('No user session found. Please log in again.');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const response = await generateInviteLink({ userId });
      if (response.success && response.data) {
        setResult(response.data);
        setInviteStatus('success');
        setInviteMessage('Invite link generated successfully.');
        onGenerated(response.data);
      } else {
        setInviteStatus('error');
        setInviteMessage('Invite limit exceeded.');
      }
    } catch (err) {
      console.error('Generate invite failed', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (open) {
      setError('');
      setInviteMessage('');
      setInviteStatus('');
      setResult({ inviteCode: '', inviteUrl: '', expiryAt: '' });
      setGenerating(false);
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <UserPlus size={18} />
                </span>
                <div>
                  <h2 className="text-base font-black tracking-tight text-slate-100">Generate Invite Link</h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    Create a personal link others can use to join
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto px-6 py-5">
              {error && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                  {error}
                </div>
              )}

              {/* Existing generate flow: generate button stays in the modal, then renders the existing share component */}
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-600 py-3.5 text-xs font-black uppercase tracking-widest text-white shadow-xl shadow-cyan-950/50 transition hover:bg-cyan-700 disabled:opacity-50"
              >
                {generating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                {generating ? 'Generating...' : 'Generate Invite Link'}
              </button>

              <SocialShareComponent
                inviteData={result}
                message={inviteMessage}
                status={inviteStatus}
              />
            </div>

            <div className="flex items-center justify-end border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const RevokeConfirmModal: React.FC<{
  open: boolean;
  code: string;
  revoking: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, code, revoking, onCancel, onConfirm }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
        onClick={onCancel}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
        >
          <div className="flex items-center gap-3 border-b border-slate-800 px-6 py-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400">
              <AlertTriangle size={18} />
            </span>
            <div>
              <h2 className="text-base font-black tracking-tight text-slate-100">Revoke invite link</h2>
              <p className="text-[11px] font-semibold text-slate-500">This can be undone only by generating a new link</p>
            </div>
          </div>

          <div className="space-y-3 px-6 py-5 text-sm font-semibold text-slate-300">
            <p>
              The link below will stop working immediately and nobody will be able to sign up with it.
            </p>
            <code className="block rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 font-mono text-xs font-bold text-cyan-300">
              {code}
            </code>
            <p className="text-xs font-semibold text-emerald-400">
              The invite credit it used will be returned to your balance, so you can generate a replacement.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={revoking}
              className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
            >
              Keep it
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={revoking}
              className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-amber-400 disabled:opacity-50"
            >
              {revoking ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
              {revoking ? 'Revoking...' : 'Revoke link'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const CandidateInviteLinkPage: React.FC = () => {
  const [invitations, setInvitations] = useState<AllInvitationByInvitedUserResponseDTO[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse<AllInvitationByInvitedUserResponseDTO> | null>(null);
  const [stats, setStats] = useState<InvitationStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [status, setStatus] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [generateOpen, setGenerateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  const userId = storage.getUserId() ?? '';

  const activeFilters: InvitationListingFilters = useMemo(
    () => ({
      status: status !== 'ALL' ? status : undefined,
      search: debouncedSearch || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [status, debouncedSearch, fromDate, toDate]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchInvitations = useCallback(async () => {
    if (!userId) {
      setError('No user session found. Please log in again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getInvitationsByInvitedUserFiltered(userId, activeFilters, page, PAGE_SIZE);
      if (response.success && response.data) {
        const pageData = response.data.page;
        setInvitations(pageData.content ?? []);
        setStats(response.data.stats ?? emptyStats);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setInvitations([]);
        setStats(emptyStats);
        setPagination(null);
        setError(response.message || 'Could not load invite links.');
      }
    } catch (err) {
      console.error('Failed to load invite links', err);
      setInvitations([]);
      setStats(emptyStats);
      setPagination(null);
      setError('An unexpected error occurred while loading invite links.');
    } finally {
      setLoading(false);
    }
  }, [userId, activeFilters, page]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, status, fromDate, toDate]);

  const resetFilters = () => {
    setSearch('');
    setStatus('ALL');
    setFromDate('');
    setToDate('');
  };

  const hasActiveFilters = Boolean(search) || status !== 'ALL' || Boolean(fromDate) || Boolean(toDate);

  const handleGenerated = (data: GenerateInviteResponseDTO) => {
    setAlert({
      variant: 'success',
      title: 'Invite link created',
      message: `Share it to invite someone new. Expires ${data.expiryAt ? formatDateTime(data.expiryAt) : 'soon'}.`,
      autoCloseMs: 5000,
      onClose: () => setAlert(null),
    });
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const response = await revokeInviteLink(revokeTarget);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Invite link revoked',
          message: `${revokeTarget} no longer works. ${response.data?.creditsRestored ? '1 invite credit was returned to your balance.' : ''}`.trim(),
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        setRevokeTarget(null);
        fetchInvitations();
      } else {
        setAlert({
          variant: 'error',
          title: 'Could not revoke link',
          message: response.message || 'This link can no longer be revoked.',
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Revoke invite failed', err);
      setAlert({
        variant: 'error',
        title: 'Could not revoke link',
        message: 'Something went wrong. Please try again.',
        onClose: () => setAlert(null),
      });
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Link2 size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Invite Link</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Share personal invite links and track who has joined
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchInvitations}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={() => setGenerateOpen(true)}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
          >
            <UserPlus size={15} /> Generate Invite Link
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Total Links"
          value={stats.total}
          icon={<Link2 size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={<Clock size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
        <StatCard
          label="Used"
          value={stats.used}
          icon={<Check size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-300"
        />
        <StatCard
          label="Registered"
          value={stats.registered}
          icon={<UserCheck size={16} />}
          accent="border-indigo-500/30 bg-indigo-500/10 text-indigo-400"
        />
        <StatCard
          label="Expired"
          value={stats.expired}
          icon={<Link2Off size={16} />}
          accent="border-slate-500/30 bg-slate-500/10 text-slate-400"
        />
        <StatCard
          label="Revoked"
          value={stats.revoked}
          icon={<Ban size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-4 md:col-span-3 xl:col-span-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                <Ticket size={16} />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                  Invite Balance
                </p>
                <p className="text-xs font-semibold text-slate-500">
                  Credits returned automatically when you revoke an unused link
                </p>
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-cyan-300">{stats.inviteBalance}</span>
              <span className="text-sm font-bold text-slate-500">
                {stats.inviteTotal > 0 ? `of ${stats.inviteTotal} granted` : 'left'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <div className="relative xl:col-span-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email, name, invite code..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition focus:border-cyan-500/50 [color-scheme:dark]"
            />
          </div>

          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition focus:border-cyan-500/50 [color-scheme:dark]"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option}
              onClick={() => setStatus(option)}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                status === option
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
              }`}
            >
              {option === 'ALL' ? 'All Status' : prettifyStatus(option)}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div key={idx} className="animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="mb-4 h-5 w-48 rounded bg-slate-800" />
              <div className="space-y-3">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-4 w-full rounded bg-slate-800/70" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : invitations.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">🔗</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No invite links found</p>
          {hasActiveFilters ? (
            <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Clear filters
            </button>
          ) : (
            <button onClick={() => setGenerateOpen(true)} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Generate your first invite link
            </button>
          )}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-cyan-400">
                  <UserPlus size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">Invite Link History</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {pagination?.totalElements ?? invitations.length} link
                    {(pagination?.totalElements ?? invitations.length) === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setGenerateOpen(true)}
                className="flex items-center gap-1.5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-cyan-400 transition hover:bg-cyan-500/20"
              >
                <Plus size={13} /> Generate
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Invitee</th>
                    <th className="px-5 py-3">Invite Code</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Joined</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3">Expires</th>
                    <th className="px-5 py-3 text-right">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((item) => {
                    const meta = statusMeta[item.invitationStatus?.toUpperCase() as keyof typeof statusMeta] ??
                      statusMeta.EXPIRED;
                    const name = fullName(item);
                    const registered = String(item.registrationStatus || '').toUpperCase() === 'REGISTERED';
                    const effectiveStatus = String(item.invitationStatus || '').toUpperCase();
                    // Revoke is offered on any unspent link, expired included: an aged-out link
                    // already burned a credit and can never register anyone, so calling it back
                    // is exactly how that credit gets recovered.
                    const canRevoke = effectiveStatus === 'ACTIVE' || effectiveStatus === 'EXPIRED';
                    return (
                      <tr
                        key={item.invitationId ?? item.inviteId ?? item.inviteCode}
                        className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-slate-800 bg-slate-950 text-[11px] font-black text-cyan-400">
                              {(name || item.email || '?').charAt(0).toUpperCase()}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold text-slate-100">{name || 'Pending invite'}</p>
                              {item.email ? (
                                <p className="flex items-center gap-1 truncate text-[11px] text-slate-500">
                                  <Mail size={11} /> {item.email}
                                </p>
                              ) : (
                                <p className="text-[11px] italic text-slate-600">Not registered yet</p>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <code className="rounded-lg border border-slate-800 bg-slate-950 px-2 py-1 font-mono text-[11px] font-bold text-cyan-300">
                              {item.inviteCode}
                            </code>
                            <CopyButton text={item.inviteCode} title="Copy invite code" />
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${statusBadge(
                              item.invitationStatus
                            )}`}
                          >
                            {prettifyStatus(item.invitationStatus) || '—'}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          {registered ? (
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${meta.cls}`}
                            >
                              <UserCheck size={11} /> {new Date(item.userCreatedAt || '').toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                              Not joined
                            </span>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <CalendarDays size={12} className="text-slate-500" /> {formatDateTime(item.createdAt)}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <Clock size={12} className="text-slate-500" /> {formatDateTime(item.expiryAt)}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <a
                                href={item.inviteUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                              >
                                <Link2Icon size={12} /> Open
                              </a>
                              <CopyButton text={item.inviteUrl} title="Copy invite link" />
                              {canRevoke && (
                                <button
                                  type="button"
                                  onClick={() => setRevokeTarget(item.inviteCode)}
                                  className="flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-amber-400 transition hover:bg-amber-500/20"
                                >
                                  <Ban size={12} /> Revoke
                                </button>
                              )}
                            </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {pagination && pagination.totalElements > 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={pagination.page}
                size={pagination.size}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={setPage}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Generate modal */}
      <GenerateInviteModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={handleGenerated}
      />
      <RevokeConfirmModal
        open={Boolean(revokeTarget)}
        code={revokeTarget ?? ''}
        revoking={revoking}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
      />
    </div>
  );
};

export default CandidateInviteLinkPage;